import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { colourForIndex, type CategoryColour } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useAuthStore, type AuthUser, type OrganisationSummary, type OrgSummary } from '@/stores/auth';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { Button, ColourPicker, Input, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from '@/features/common/ui';

interface Allowance {
  created: number;
  /** null for platform staff: no limit. */
  limit: number | null;
  remaining: number | null;
}

interface CreatedBook {
  orgId: string;
  user: AuthUser;
  orgs: OrgSummary[];
  organisations?: OrganisationSummary[];
}

/**
 * A second book, from the switcher.
 *
 * ── WHY THIS EXISTED ONLY AT REGISTRATION ──
 * The first book is made with the account, and for a long time that was the
 * only way one got made: the API had the schema and the provisioning code for
 * "create another org" and no route calling either. Anyone whose affairs are
 * an LLP and a freelance practice - the very person the Overview was built
 * for - had to register twice.
 *
 * ── WHAT HAPPENS ON SUCCESS ──
 * The server answers with the same shape as sign-in plus the new id, so the
 * session is replaced in one step and the app switches to the new book
 * through `useSwitchOrg`, which is the one place the "clear every cached query
 * first" rule lives. Landing in the new, empty book is deliberate: it is what
 * the person just asked for, and its empty dashboard says so.
 */
/**
 * `organisationId`: the shelf the book lands on. The switcher passes the
 * active book's organisation when the person owns it; the Organisation
 * screen passes its own. Absent, the API picks the first the person owns.
 */
export function NewBookSheet({ onClose, organisationId }: { onClose: () => void; organisationId?: string }) {
  useEscapeToClose(onClose);
  const setSession = useAuthStore((s) => s.setSession);
  const switchTo = useSwitchOrg();
  const [name, setName] = useState('');
  /**
   * Preselected by the same rotation the server would use - the second book
   * is amber - so the picker shows what the book WILL be rather than a blank,
   * and a person who never touches it gets the same answer as one who never
   * saw it. Re-seeded once the allowance arrives, and only until they choose.
   */
  const [colour, setColour] = useState<CategoryColour>(() => colourForIndex(1));
  const [chose, setChose] = useState(false);

  const organisationName = useAuthStore((s) => s.organisations.find((o) => o.id === organisationId)?.name ?? null);
  const allowance = useQuery({
    queryKey: ['book-allowance'],
    queryFn: () => api.get<Allowance>('/orgs/allowance'),
  });
  useEffect(() => {
    if (allowance.data && !chose) setColour(colourForIndex(allowance.data.created));
  }, [allowance.data, chose]);

  const create = useMutation({
    mutationFn: (input: { name: string; colour: CategoryColour; organisationId?: string }) => api.post<CreatedBook>('/orgs', input),
    onSuccess: (data) => {
      setSession(data.user, data.orgs, data.organisations);
      onClose();
      switchTo(data.orgId);
    },
  });

  const remaining = allowance.data?.remaining ?? null;
  const limit = allowance.data?.limit ?? null;
  const exhausted = remaining !== null && remaining <= 0;

  return (
    <Sheet labelledBy="new-book-title" onClose={onClose}>
      <SheetHeader titleId="new-book-title" title="New book" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({ name: name.trim(), colour, ...(organisationId ? { organisationId } : {}) });
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            A book is a separate set of accounts under this login - its own bank accounts, invoices, GST
            setting and returns. A GST-registered firm and a freelance practice are two books, and the
            Overview adds them up.
            {organisationName ? (
              <>
                {' '}
                It goes in <strong className="font-medium text-foreground">{organisationName}</strong>.
              </>
            ) : null}
          </p>
          <div className="space-y-1">
            <label htmlFor="new-book-name" className="text-sm font-medium">
              Name
            </label>
            <span aria-hidden="true" className="text-destructive">
              {' '}
              *
            </span>
            <Input
              id="new-book-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={160}
              placeholder="Personal, or the firm's name"
              disabled={exhausted}
            />
          </div>

          <div className="space-y-1">
            <span className="text-sm font-medium">Colour</span>
            <p className="text-xs text-muted-foreground">
              How this book is marked in the switcher, on the Overview and in the Inbox.
            </p>
            <ColourPicker
              label="Book colour"
              value={colour}
              onChange={(next) => {
                setChose(true);
                setColour(next);
              }}
            />
          </div>

          {/*
            The allowance, stated before the button is pressed rather than as
            the reason it did nothing. "1 of 2 free books used" is a fact a
            person can plan around; a refusal after typing a name is not.
          */}
          {allowance.data ? (
            <p className="text-xs text-muted-foreground" data-testid="book-allowance">
              {limit === null
                ? 'No limit on this account.'
                : `${allowance.data.created} of ${limit} free ${limit === 1 ? 'book' : 'books'} used.`}
            </p>
          ) : null}

          {exhausted ? (
            <p role="alert" className="text-sm text-destructive">
              Every account can keep {limit === 2 ? 'two' : limit} free {limit === 1 ? 'book' : 'books'}, and this
              one already has them. More books are coming as a paid plan.
            </p>
          ) : null}

          {create.error ? (
            <p role="alert" className="text-sm text-destructive">
              {create.error instanceof ApiError ? create.error.message : 'Something went wrong'}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" disabled={create.isPending || exhausted || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create book'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
